import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import csvParse from 'csv-parse/lib/sync';
import { roundMinutes, mapSelect } from 'app/utils/parsers';
import HeaderThin from 'app/components/layout/HeaderThin';
import { getSeasonTagInSelectFormat } from 'app/ducks/organisations/selectors';
import { getCompetitionsByIdInSelectFormat } from 'app/ducks/competitions/selectors';
import MapFields from './MapFields';
import UploadFile from './UploadFile';
import StaticData from './StaticData';
import ReviewImport from './ReviewImport';
import validateInput from './StaticDataValidator';

interface IImportEventsProps {
  appReady: boolean;
  actions: any;
  organisations: any;
  competitions: any;
  match: any;
  errors: any;
}

interface IImportEventsState {
  importStatus: string;
  fileContent: any;
  availableFields: any;
  exampleRecord: any;
  errors: any;
  staticData: any;
}
export default class ImportEvents extends React.Component<
  IImportEventsProps,
  IImportEventsState
> {
  static defaultProps: IImportEventsProps = {
    appReady: false,
    actions: undefined,
    organisations: {},
    competitions: {},
    match: undefined,
    errors: undefined,
  };

  state: IImportEventsState = {
    importStatus: 'setup',
    fileContent: null,
    availableFields: [],
    exampleRecord: null,
    errors: {},
    staticData: {
      amount: undefined,
      paymentCategory: {
        id: 'event',
        name: 'Standard',
      },
      paymentType: 'organisation',
      dueConfig: 2,
      seasonTag: undefined,
      activeAt: roundMinutes(new Date()).toString(),
      planIdentifier: undefined,
      ignoreIdentifier: 'Bye',
      competition: undefined,
      descriptionPrefix: 'Round',
    },
  };

  componentDidMount = (): void => {
    this.props.actions.getAllCompetitions();
  }

  extractHeaderColumns = (fileContent): any => {
    const records = csvParse(fileContent, {
      columns: true,
      skip_empty_lines: true,
      skip_lines_with_error: true,
    });
    const exampleRecord = records[0];
    const headerColumns = _.keys(exampleRecord);
    return [exampleRecord, headerColumns];
  };

  changeFileContent = (newContent): void => {
    const [exampleRecord, headerColumns] = this.extractHeaderColumns(
      newContent,
    );
    // console.log(exampleRecord, headerColumns);
    this.setState({
      fileContent: newContent,
      availableFields: headerColumns,
      exampleRecord,
    });
  };

  isValid = (): boolean => {
    const { errors, isValid } = validateInput(this.state.staticData);
    this.setState({ errors });
    return isValid;
  };

  handleImportStatus = (status): void => {
    if (this.isValid()) {
      this.setState({ importStatus: status });
    }
  };

  handleChange = (e): void => {
    delete this.state.errors[e.target.name];
    const state = {
      staticData: {
        ...this.state.staticData,
        [e.target.name]: e.target.value,
        errors: this.state.errors,
      },
    };

    this.setState(state);
  };

  handlePaymentCategoryChange = (e): void => {
    delete this.state.errors.paymentCategory;
    if (e && e.id !== null) {
      this.setState({
        staticData: {
          ...this.state.staticData,
          paymentCategory: e,
        },
      });
    }
  };

  handleCompetitionChange = (e): void => {
    delete this.state.errors.competition;
    if (e && e.id !== null) {
      this.setState({
        staticData: { ...this.state.staticData, competition: e },
      });
    }
  };

  handleDueConfigChange = (e): void => {
    delete this.state.errors[e.target.name];
    const state = {
      staticData: {
        ...this.state.staticData,
        dueConfig: parseInt(e.target.value, 10),
        errors: this.state.errors,
      },
    };

    this.setState(state);
  };

  handleSeasonChange = (e): void => {
    delete this.state.errors.seasonTag;
    const state = {
      staticData: {
        ...this.state.staticData,
        seasonTag: e,
        planIdentifier: e,
        errors: this.state.errors,
      },
    };

    this.setState(state);
  };

  handleAmountChange = (values): void => {
    const { formattedValue, value } = values;
    delete this.state.errors.amount;
    this.setState({
      staticData: {
        ...this.state.staticData,
        amount: formattedValue,
        floatAmount: value,
      },
    });
  };

  handleChangeTime = (e): void => {
    delete this.state.errors.activeAt;
    this.setState({
      staticData: {
        ...this.state.staticData,
        activeAt: e[0].toString(),
      },
    });
  };

  renderSection = (): React.ReactNode => {
    if (this.props.match.path === '/events/imports/:importId') {
      return <ReviewImport {...this.props} />;
    }
    if (this.state.importStatus === 'setup') {
      return (
        <StaticData
          {...this.props}
          {...this.state}
          {...this.state.staticData}
          errors={this.state.errors}
          handleChange={this.handleChange}
          handleDueConfigChange={this.handleDueConfigChange}
          handleSeasonChange={this.handleSeasonChange}
          handleAmountChange={this.handleAmountChange}
          handleChangeTime={this.handleChangeTime}
          handlePaymentCategoryChange={this.handlePaymentCategoryChange}
          handleCompetitionChange={this.handleCompetitionChange}
          changeFileContent={this.changeFileContent}
          handleImportStatus={this.handleImportStatus}
          seasonTags={getSeasonTagInSelectFormat(
            this.props.organisations.currentOrganisation.seasonTags,
          )}
          competitions={this.props.competitions.competitionsByIdInSelectFormat}
        />
      );
    }
    if (this.state.importStatus === 'upload') {
      return (
        <UploadFile
          {...this.props}
          {...this.state}
          changeFileContent={this.changeFileContent}
          handleImportStatus={this.handleImportStatus}
        />
      );
    }
    if (this.state.importStatus === 'mapping') {
      return (
        <MapFields
          {...this.props}
          {...this.state}
          fileContent={this.state.fileContent}
          handleImportStatus={this.handleImportStatus}
        />
      );
    }
    return null;
  };

  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              breadcrumb="Events"
              breadcrumbLink="/events"
              title="Import Events"
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12 col-lg-10 col-xl-8">
            {this.renderSection()}
          </div>
        </div>
      </div>
    );
  }
}
